import { useMutation, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { localeLabels, type Locale } from '@sonasi/shared-types';

import { gqlClient } from '@/api';
import { saveMessage } from './apps';

/**
 * "Translate with AI", in two steps: ask what it would do, then do it.
 *
 * The plan is a mutation-shaped call rather than a query on purpose: it is
 * asked for on a press, shown once, and must never be served from a cache —
 * a plan from before somebody typed a Japanese tagline would offer to fill a
 * block that is no longer empty. (The server re-checks at write time anyway;
 * this is about the number the person sees.)
 */

/**
 * An app's listing, a post, a studio legal document (id `terms` / `privacy`),
 * or the site copy (id `site`).
 */
export type TranslatableKind = 'app' | 'post' | 'legal' | 'site';

export interface TranslationBlock {
  block: string;
  characters: number;
}

export interface TranslationLocalePlan {
  locale: string;
  fill: TranslationBlock[];
  skip: string[];
}

export interface TranslationPlan {
  locales: TranslationLocalePlan[];
  characters: number;
  tooLarge: boolean;
  /** The record is live: what is written goes on the website at once. */
  published: boolean;
  ready: boolean;
  notReady: string | null;
}

/** One language's translation, held by the screen until it is applied. */
export interface TranslationLocaleDraft {
  locale: string;
  /** Block name → translated value. Handed back to `applyTranslation` untouched. */
  blocks: Record<string, unknown>;
  /** Blocks in this draft — the design's "12 fields", in the unit `apply` reports. */
  fields: number;
  /** Blocks that already had something in this language when drafted. */
  written: string[];
  failed: string | null;
}

export type ApplyMode = 'fill' | 'overwrite';

export interface TranslationApplied {
  locale: string;
  written: number;
  kept: number;
}

const PLAN = gql`
  query TranslationPlan($input: TranslateInput!) {
    translationPlan(input: $input) {
      locales { locale fill { block characters } skip }
      characters
      tooLarge
      published
      ready
      notReady
    }
  }
`;

const DRAFT = gql`
  mutation Translate($input: TranslateInput!) {
    translate(input: $input) { locale blocks fields written failed }
  }
`;

const APPLY = gql`
  mutation ApplyTranslation($input: ApplyTranslationInput!) {
    applyTranslation(input: $input) { locale written kept }
  }
`;

/*
 * Both enums travel *out* upper-cased — GraphQL takes a member name and maps
 * it to the value the server registered. What comes *back* on `locale`
 * fields is a plain string, already lower case: nothing to turn round.
 */
const wire = (kind: TranslatableKind, id: string, into: readonly Locale[]) => ({
  input: { kind: kind.toUpperCase(), id, into: into.map((locale) => locale.toUpperCase()) },
});

export const usePlanTranslation = () =>
  useMutation({
    mutationFn: async (args: { kind: TranslatableKind; id: string; into: readonly Locale[] }) => {
      const { translationPlan } = await gqlClient.request<{ translationPlan: TranslationPlan }>(
        PLAN,
        wire(args.kind, args.id, args.into),
      );
      return translationPlan;
    },
  });

/**
 * Asks the model for every language, **one request per language, in turn**,
 * and writes nothing: each answer is a draft the screen holds until the
 * person says how to apply it.
 *
 * ⚠️ One call per language, not one call for all. A model can take a minute
 * or two on a long listing; three in a row is past the five minutes Node
 * allows a request, and the person is looking at a spinner the whole time.
 * `onLanguage` lets the modal show a language landing while the next is
 * asked for, and `shouldStop` is the modal's Stop button: checked before each
 * language, so a run stops between languages rather than mid-answer.
 */
export const useDraftTranslation = () =>
  useMutation({
    mutationFn: async (args: {
      kind: TranslatableKind;
      id: string;
      into: readonly Locale[];
      onLanguage?: (done: TranslationLocaleDraft[]) => void;
      shouldStop?: () => boolean;
    }) => {
      const done: TranslationLocaleDraft[] = [];
      for (const locale of args.into) {
        if (args.shouldStop?.()) break;
        try {
          const { translate } = await gqlClient.request<{ translate: TranslationLocaleDraft[] }>(
            DRAFT,
            wire(args.kind, args.id, [locale]),
          );
          done.push(...translate);
        } catch (error) {
          // A refusal for this language — throttled, or the provider went away
          // mid-run — is this language's result, not the end of the run.
          done.push({ locale, blocks: {}, fields: 0, written: [], failed: saveMessage(error) });
        }
        // A copy, so React sees a new array each time.
        args.onLanguage?.([...done]);
      }
      return done;
    },
  });

/**
 * Writes the drafts, one language per request, under the mode chosen — then
 * drops every cached copy of the record so the editor reloads the languages
 * that were just written and the tabs' percentages move.
 */
export const useApplyTranslation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      kind: TranslatableKind;
      id: string;
      drafts: TranslationLocaleDraft[];
      mode: ApplyMode;
    }) => {
      const results: TranslationApplied[] = [];
      for (const draft of args.drafts) {
        if (draft.failed || Object.keys(draft.blocks).length === 0) continue;
        try {
          const { applyTranslation } = await gqlClient.request<{ applyTranslation: TranslationApplied }>(APPLY, {
            input: {
              kind: args.kind.toUpperCase(),
              id: args.id,
              locale: draft.locale.toUpperCase(),
              blocks: draft.blocks,
              mode: args.mode.toUpperCase(),
            },
          });
          results.push(applyTranslation);
        } catch (error) {
          /*
           * Said with the language in front, and with what did land after it,
           * because by now the languages before this one are written —
           * `saveMessage` for a dropped connection says "Your changes have
           * not been saved", true of this language and false of those. Only
           * languages that wrote something are named: a FILL that kept every
           * field of 日本語 did not write 日本語.
           */
          const wrote = results.filter((r) => r.written > 0);
          const before = wrote.reduce((sum, r) => sum + r.written, 0);
          const name = localeLabels[draft.locale as Locale] ?? draft.locale;
          const landed = wrote.map((r) => localeLabels[r.locale.toLowerCase() as Locale] ?? r.locale).join(', ');
          throw new Error(
            before > 0
              ? `${name}: ${saveMessage(error)} (${before} ${before === 1 ? 'field' : 'fields'} in ${landed} ${before === 1 ? 'was' : 'were'} already written.)`
              : `${name}: ${saveMessage(error)}`,
          );
        }
      }
      return results;
    },
    onSettled: (_result, _error, args) => {
      // Even a half-applied run wrote something: reload.
      for (const key of reloadAfter(args.kind, args.id)) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
};

/**
 * What to drop from the cache once a translation is written.
 *
 * The legal documents are one query for every kind and language, and the
 * list of apps showing a document changes the moment a language exists —
 * both have to go, or the tab keeps saying "not written" over a row that is.
 */
const reloadAfter = (kind: TranslatableKind, id: string): readonly (readonly string[])[] => {
  if (kind === 'app') return [['adminApp', id]];
  if (kind === 'post') return [['adminPost', id]];
  if (kind === 'site') return [['adminSiteContent']];
  return [['studioLegal'], ['appsUsingStudioLegal']];
};
